import { useCallback, useEffect, useState } from 'react';
import {
  LEGACY_VIEW_MODE_KEYS,
  usePreferences,
} from '../contexts/PreferencesContext';

export type ViewMode = 'grid' | 'list';

const VIEW_MODES: readonly ViewMode[] = ['grid', 'list'];

export const DEFAULT_VIEW_MODE: ViewMode = 'grid';

export const isViewMode = (value: unknown): value is ViewMode =>
  typeof value === 'string' && (VIEW_MODES as readonly string[]).includes(value);

export const loadViewMode = (
  storageKey: string,
  defaultMode: ViewMode = DEFAULT_VIEW_MODE,
): ViewMode => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (isViewMode(stored)) return stored;
  } catch {
    // Inaccessible localStorage should not block rendering.
  }
  return defaultMode;
};

export const persistViewMode = (storageKey: string, viewMode: ViewMode): void => {
  try {
    localStorage.setItem(storageKey, viewMode);
  } catch {
    // The in-memory selection still works for the active session.
  }
};

export interface ViewModeControls {
  /** True when this surface has its own choice rather than following the default. */
  isOverride: boolean;
  /** Drop the override so the surface follows the account default again. */
  useDefault: () => void;
}

/**
 * A surface's grid/list choice.
 *
 * With a PreferencesProvider mounted, a surface with no entry in the
 * ``view_modes`` namespace follows the account-wide ``default_view_mode``;
 * choosing a view creates an override, and ``useDefault()`` removes it.
 * Without a provider (component tests, unauthenticated screens) it falls back
 * to the legacy per-surface localStorage key.
 *
 * ``surfaceKey`` is a short identifier ('habits', 'timers', …) — the legacy
 * storage key it maps to lives in LEGACY_VIEW_MODE_KEYS.
 */
export const usePersistentViewMode = (
  surfaceKey: string,
  defaultMode: ViewMode = DEFAULT_VIEW_MODE,
): [ViewMode, (viewMode: ViewMode) => void, ViewModeControls] => {
  const preferences = usePreferences();
  const legacyKey = LEGACY_VIEW_MODE_KEYS[surfaceKey] || surfaceKey;

  const [localViewMode, setLocalViewMode] = useState<ViewMode>(() =>
    loadViewMode(legacyKey, defaultMode),
  );

  // Providerless fallback only. Writing under the provider would create a
  // default-valued legacy key on a fresh browser, which the one-time migration
  // would then mistake for a real preference worth lifting.
  useEffect(() => {
    if (preferences) return;
    persistViewMode(legacyKey, localViewMode);
  }, [legacyKey, localViewMode, preferences]);

  const overrides = (preferences?.settings.view_modes || {}) as Record<string, unknown>;
  const override = overrides[surfaceKey];
  const hasOverride = isViewMode(override);
  const accountDefault = preferences?.settings.default_view_mode;

  const viewMode: ViewMode = preferences
    ? (hasOverride ? (override as ViewMode) : accountDefault) || defaultMode
    : localViewMode;

  const setViewMode = useCallback(
    (next: ViewMode) => {
      setLocalViewMode(next);
      if (preferences) {
        preferences.setNamespace('view_modes', { ...overrides, [surfaceKey]: next });
      }
    },
    // overrides is a fresh object each render; depend on its serialization
    // instead so the callback is stable between real changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preferences, surfaceKey, JSON.stringify(overrides)],
  );

  const useDefault = useCallback(() => {
    if (!preferences) {
      return;
    }
    const next = { ...overrides };
    delete next[surfaceKey];
    preferences.setNamespace('view_modes', next);
    if (accountDefault) {
      setLocalViewMode(accountDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, surfaceKey, accountDefault, JSON.stringify(overrides)]);

  return [
    viewMode,
    setViewMode,
    { isOverride: preferences ? hasOverride : true, useDefault },
  ];
};

export default usePersistentViewMode;
